import React, { useEffect, useState, useRef } from 'react';
import AppLayout from '../../components/AppLayout';
import api from '../../api/axiosInstance';
import { extractStrategyRows, getActiveStrategies, normalizeStrategies } from '../../api/strategyLibraryService';
import './BatchUpload.css';

/* ─────────────────────────────────────────────────────────────────
   MARKET DATA TAB
───────────────────────────────────────────────────────────────── */
const MARKET_CARDS = [
  { key: 'nifty50', icon: '📈', label: 'Nifty 50', field: 'nifty50File' },
  { key: 'fo',      icon: '📉', label: 'F&O Data', field: 'foFile'      },
  { key: 'others',  icon: '📊', label: 'Others',   field: 'othersFile'  },
  { key: 'cas',     icon: '🕔', label: 'CAS',      field: 'casFile'     },
];

const STRATEGY_CARDS = [
  { key: 'bottomfishing',         icon: '🎣', label: 'Bottom Fishing',        type: 'bottomfishing' },
  { key: 'parabolic',             icon: '🚀', label: 'Parabolic',             type: 'parabolic' },
  { key: 'exit',                  icon: '🚪', label: 'Exit',                  type: 'exit' },
  { key: 'fundamental',           icon: '📐', label: 'Fundamental',           type: 'fundamental' },
  { key: 'mfibo',                 icon: '🌀', label: 'M-Fibo',               type: 'mfibo' },
  { key: 'rsc',                   icon: '📡', label: 'RSC',                   type: 'rsc' },
  { key: 'dailyabcandlepositive', icon: '🟢', label: 'Daily AB Candle +ve',   type: 'dailyabcandlepositive' },
  { key: 'dailyabcandlenegative', icon: '🔴', label: 'Daily AB Candle -ve',   type: 'dailyabcandlenegative' },
];

const toStrategyCard = (strategy) => ({
  key: strategy.slug,
  icon: strategy.icon || '🎯',
  label: strategy.name,
  type: strategy.slug,
});

// Backend returns errors under { error, details, message } — pick the most informative one.
const describeUploadError = (e) => {
  const data = e?.response?.data;
  if (data) {
    const parts = [data.error, data.message, data.details].filter(Boolean);
    if (parts.length) return parts.join(' — ');
  }
  return e?.message || 'Upload failed';
};

/* ─────────────────────────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────────────────────────── */
function ResultBox({ result, onClear }) {
  if (!result) return null;
  const isErr = result.isError;
  return (
    <div className={`bu-result-box ${isErr ? 'bu-result-box--error' : 'bu-result-box--success'}`}>
      <div className="bu-result-box__header">
        <span>{isErr ? '❌' : '✅'} {result.title}</span>
        <button className="bu-result-box__close" onClick={onClear}>✕</button>
      </div>
      {result.message && <p className="bu-result-box__msg">{result.message}</p>}
      {result.stats && (
        <div className="bu-stats-row">
          {Object.entries(result.stats).map(([k, v]) => (
            <div key={k} className="bu-stat-chip">
              <div className="bu-stat-chip__val">{v}</div>
              <div className="bu-stat-chip__lbl">{k}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ProgressBar({ pct }) {
  if (pct === null) return null;
  return (
    <div className="bu-progress-wrap">
      <div className="bu-progress-bar" style={{ width: `${pct}%` }} />
    </div>
  );
}

function FileCard({ icon, label, file, onSelect, onUpload, uploading }) {
  const inputRef = useRef(null);
  return (
    <div className={`bu-file-card ${file ? 'bu-file-card--ready' : ''}`}>
      <div className="bu-file-card__header">
        <span className="bu-file-card__icon">{icon}</span>
        <span className="bu-file-card__title">{label}</span>
      </div>
      <label className="bu-file-picker">
        <input
          ref={inputRef}
          type="file"
          accept=".csv,text/csv"
          className="bu-file-input"
          onChange={(e) => onSelect(e.target.files[0] || null)}
        />
        <span className="bu-file-picker__btn">📁 Choose CSV</span>
        <span className="bu-file-picker__name">{file ? file.name : 'No file selected'}</span>
      </label>
      <p className={`bu-file-status ${file ? 'bu-file-status--ok' : ''}`}>
        {file ? `✓ ${file.name}` : 'No file selected'}
      </p>
      <button
        className="bu-btn bu-btn--outline"
        disabled={!file || uploading}
        onClick={onUpload}
      >
        {uploading ? '⏳ Uploading…' : '⬆ Upload'}
      </button>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
   TAB 1 — MARKET DATA
───────────────────────────────────────────────────────────────── */
function MarketTab() {
  const [files, setFiles]           = useState({ nifty50: null, fo: null, others: null });
  const [uploading, setUploading]   = useState({});
  const [batchUploading, setBatch]  = useState(false);
  const [progress, setProgress]     = useState(null);
  const [result, setResult]         = useState(null);

  const setFile = (key, file) => setFiles(prev => ({ ...prev, [key]: file }));
  const allReady = MARKET_CARDS.every(c => files[c.key]);

  const uploadSingle = async (key) => {
    const card = MARKET_CARDS.find(c => c.key === key);
    const fd = new FormData();
    fd.append('csvFile', files[key]);
    setUploading(p => ({ ...p, [key]: true }));
    setResult(null);
    try {
      const { data } = await api.post(`/csv/upload-market/${key}`, fd);
      setResult({
        title: `${card.label} uploaded`,
        message: data.message,
        stats: {
          Inserted: data.results?.inserted ?? data.inserted ?? '—',
          Updated:  data.results?.updated  ?? data.updated  ?? '—',
        },
      });
    } catch (e) {
      setResult({ isError: true, title: 'Upload failed', message: describeUploadError(e) });
    } finally {
      setUploading(p => ({ ...p, [key]: false }));
    }
  };

  const batchUpload = async () => {
    setBatch(true);
    setProgress(10);
    setResult(null);
    try {
      const fd = new FormData();
      MARKET_CARDS.forEach(c => fd.append(c.field, files[c.key]));
      setProgress(40);
      const { data } = await api.post('/csv/batch-upload', fd);
      setProgress(100);
      setResult({
        title: 'Batch upload complete',
        message: data.message,
        stats: {
          Inserted: data.results?.inserted ?? '—',
          Updated:  data.results?.updated  ?? '—',
          Total:    data.results?.total    ?? '—',
        },
      });
    } catch (e) {
      setResult({ isError: true, title: 'Batch upload failed', message: describeUploadError(e) });
    } finally {
      setBatch(false);
      setTimeout(() => setProgress(null), 1200);
    }
  };

  return (
    <div className="bu-tab-body">
      <div className="bu-action-bar">
        <span className="bu-action-label">Upload market CSV files individually or all at once.</span>
        <button
          className={`bu-btn bu-btn--primary ${allReady ? 'bu-btn--green' : ''}`}
          disabled={!allReady || batchUploading}
          onClick={batchUpload}
        >
          {batchUploading
            ? '⏳ Uploading…'
            : allReady
              ? '🚀 Batch Upload All 3'
              : `🚀 Batch Upload (${MARKET_CARDS.filter(c => files[c.key]).length}/4)`}
        </button>
      </div>

      <div className="bu-grid">
        {MARKET_CARDS.map(c => (
          <FileCard
            key={c.key}
            icon={c.icon}
            label={c.label}
            file={files[c.key]}
            onSelect={(f) => setFile(c.key, f)}
            onUpload={() => uploadSingle(c.key)}
            uploading={!!uploading[c.key]}
          />
        ))}
      </div>

      <ProgressBar pct={progress} />
      <ResultBox result={result} onClear={() => setResult(null)} />
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
   TAB 2 — STRATEGIES
───────────────────────────────────────────────────────────────── */
function StrategyTab() {
  const [strategyCards, setStrategyCards] = useState(STRATEGY_CARDS);
  const [strategyLoading, setStrategyLoading] = useState(true);
  const [strategyError, setStrategyError] = useState('');
  const init = Object.fromEntries(strategyCards.map(c => [c.key, null]));
  const [files, setFiles]          = useState(init);
  const [uploading, setUploading]  = useState({});
  const [batchUploading, setBatch] = useState(false);
  const [progress, setProgress]    = useState(null);
  const [result, setResult]        = useState(null);

  useEffect(() => {
    let mounted = true;
    getActiveStrategies()
      .then(({ data }) => {
        if (!mounted) return;
        const rows = normalizeStrategies(extractStrategyRows(data)).filter(s => s.slug);
        if (rows.length) {
          const cards = rows.map(toStrategyCard);
          setStrategyCards(cards);
          setFiles(prev => Object.fromEntries(cards.map(c => [c.key, prev[c.key] || null])));
        }
      })
      .catch((err) => mounted && setStrategyError(err.response?.data?.message || err.message || 'Using default strategy list'))
      .finally(() => mounted && setStrategyLoading(false));
    return () => { mounted = false; };
  }, []);

  const setFile = (key, file) => setFiles(prev => ({ ...prev, [key]: file }));
  const readyCount = strategyCards.filter(c => files[c.key]).length;
  const allReady   = strategyCards.length > 0 && readyCount === strategyCards.length;

  const uploadSingle = async (key) => {
    const card = strategyCards.find(c => c.key === key);
    const fd = new FormData();
    fd.append('csvFile', files[key]);
    setUploading(p => ({ ...p, [key]: true }));
    setResult(null);
    try {
      const { data } = await api.post(`/csv/upload-strategy/${card.type}`, fd);
      setResult({
        title: `${card.label} uploaded`,
        message: data.message,
        stats: {
          Inserted: data.results?.inserted ?? data.inserted ?? '—',
          Updated:  data.results?.updated  ?? data.updated  ?? '—',
        },
      });
    } catch (e) {
      setResult({ isError: true, title: 'Upload failed', message: describeUploadError(e) });
    } finally {
      setUploading(p => ({ ...p, [key]: false }));
    }
  };

  const batchUpload = async () => {
    setBatch(true);
    setProgress(10);
    setResult(null);
    try {
      const fd = new FormData();
      strategyCards.forEach(c => fd.append(c.key, files[c.key]));
      setProgress(40);
      const { data } = await api.post('/csv/strategy-batch-upload', fd);
      setProgress(100);
      setResult({
        title: 'Strategy batch upload complete',
        message: data.message,
        stats: {
          Inserted: data.results?.inserted ?? '—',
          Updated:  data.results?.updated  ?? '—',
          Total:    data.results?.total    ?? '—',
        },
      });
    } catch (e) {
      setResult({ isError: true, title: 'Batch upload failed', message: describeUploadError(e) });
    } finally {
      setBatch(false);
      setTimeout(() => setProgress(null), 1200);
    }
  };

  return (
    <div className="bu-tab-body">
      <div className="bu-action-bar">
        <span className="bu-action-label">
          {strategyLoading ? 'Loading active strategies...' : `Upload individual strategy CSVs or all ${strategyCards.length} at once.`}
          {strategyError && ` ${strategyError}`}
        </span>
        <button
          className={`bu-btn bu-btn--primary ${allReady ? 'bu-btn--green' : ''}`}
          disabled={!allReady || batchUploading}
          onClick={batchUpload}
        >
          {batchUploading
            ? '⏳ Uploading…'
            : allReady
              ? `🎯 Batch Upload All ${strategyCards.length}`
              : `🎯 Batch Upload (${readyCount}/${strategyCards.length})`}
        </button>
      </div>

      <div className="bu-grid">
        {strategyCards.map(c => (
          <FileCard
            key={c.key}
            icon={c.icon}
            label={c.label}
            file={files[c.key]}
            onSelect={(f) => setFile(c.key, f)}
            onUpload={() => uploadSingle(c.key)}
            uploading={!!uploading[c.key]}
          />
        ))}
      </div>

      <ProgressBar pct={progress} />
      <ResultBox result={result} onClear={() => setResult(null)} />
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
   TAB 3 — NAKSHATRA
───────────────────────────────────────────────────────────────── */
function NakshatraTab() {
  const [file, setFile]          = useState(null);
  const [replaceAll, setReplace] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress]  = useState(null);
  const [result, setResult]      = useState(null);
  const inputRef = useRef(null);

  const upload = async () => {
    if (!file) return;
    setUploading(true);
    setProgress(20);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append('csvFile', file);
      fd.append('replaceAll', replaceAll ? 'true' : 'false');
      setProgress(60);
      const { data } = await api.post('/csv/upload-nakshatra', fd);
      setProgress(100);
      setResult({
        title: 'Nakshatra data uploaded',
        message: data.message,
        stats: {
          Inserted: data.results?.inserted ?? data.inserted ?? '—',
          Updated:  data.results?.updated  ?? data.updated  ?? '—',
          Total:    data.results?.total    ?? '—',
        },
      });
    } catch (e) {
      setResult({ isError: true, title: 'Upload failed', message: describeUploadError(e) });
    } finally {
      setUploading(false);
      setTimeout(() => setProgress(null), 1200);
    }
  };

  return (
    <div className="bu-tab-body">
      <div className="bu-special-panel">
        {/* Left: upload card */}
        <div className="bu-special-card">
          <div className="bu-special-title">🌟 Nakshatra Stocks Upload</div>

          <div className="bu-format-hint">
            Required columns: Rashi, Nakshatra, Stock_Name, Symbol, N_Sector
          </div>

          <label className="bu-file-picker">
            <input
              ref={inputRef}
              type="file"
              accept=".csv,text/csv"
              className="bu-file-input"
              onChange={(e) => setFile(e.target.files[0] || null)}
            />
            <span className="bu-file-picker__btn">📁 Choose CSV</span>
            <span className="bu-file-picker__name">{file ? file.name : 'No file selected'}</span>
          </label>
          <p className={`bu-file-status ${file ? 'bu-file-status--ok' : ''}`}>
            {file ? `✓ ${file.name}` : 'No file selected'}
          </p>

          <label className="bu-checkbox-row">
            <input
              type="checkbox"
              checked={replaceAll}
              onChange={(e) => setReplace(e.target.checked)}
            />
            Replace all existing records
          </label>

          <button
            className="bu-btn bu-btn--primary"
            disabled={!file || uploading}
            onClick={upload}
          >
            {uploading ? '⏳ Uploading…' : '⬆ Upload Nakshatra CSV'}
          </button>

          <ProgressBar pct={progress} />
          <ResultBox result={result} onClear={() => setResult(null)} />
        </div>

        {/* Right: format guide */}
        <div className="bu-special-card">
          <div className="bu-special-title">📋 CSV Format Guide</div>
          <p className="bu-guide-text">
            Upload a CSV with exactly these column headers (case-sensitive):
          </p>
          <div className="bu-format-hint">
            Rashi, Nakshatra, Stock_Name, Symbol, N_Sector
          </div>
          <div className="bu-sample-table-wrap">
            <table className="bu-sample-table">
              <thead>
                <tr>
                  {['Rashi', 'Nakshatra', 'Stock_Name', 'Symbol', 'N_Sector'].map(h => (
                    <th key={h}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Mesha</td><td>Ashwini</td><td>Infosys Ltd</td><td>INFY</td><td>IT</td>
                </tr>
                <tr>
                  <td>Vrishabha</td><td>Rohini</td><td>HDFC Bank</td><td>HDFCBANK</td><td>Banking</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
   TAB 4 — SECTORIAL
───────────────────────────────────────────────────────────────── */
function SectorialTab() {
  const [file, setFile]           = useState(null);
  const [replaceAll, setReplace]  = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress]   = useState(null);
  const [result, setResult]       = useState(null);
  const inputRef = useRef(null);

  const upload = async () => {
    if (!file) return;
    setUploading(true);
    setProgress(20);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append('csvFile', file);
      fd.append('replaceAll', replaceAll ? 'true' : 'false');
      setProgress(60);
      const { data } = await api.post('/csv/upload-sectorial', fd);
      setProgress(100);
      setResult({
        title: 'Sectorial data uploaded',
        message: data.message,
        stats: {
          Inserted: data.results?.inserted ?? data.inserted ?? '—',
          Updated:  data.results?.updated  ?? data.updated  ?? '—',
          Total:    data.results?.total    ?? '—',
        },
      });
    } catch (e) {
      setResult({ isError: true, title: 'Upload failed', message: describeUploadError(e) });
    } finally {
      setUploading(false);
      setTimeout(() => setProgress(null), 1200);
    }
  };

  return (
    <div className="bu-tab-body">
      <div className="bu-special-panel">
        {/* Left: upload card */}
        <div className="bu-special-card">
          <div className="bu-special-title">🏭 Sectorial Stocks Upload</div>

          <div className="bu-format-hint">
            Required columns: Company Name, Industry, Symbol, Series, ISIN Code
          </div>

          <label className="bu-file-picker">
            <input
              ref={inputRef}
              type="file"
              accept=".csv,text/csv"
              className="bu-file-input"
              onChange={(e) => setFile(e.target.files[0] || null)}
            />
            <span className="bu-file-picker__btn">📁 Choose CSV</span>
            <span className="bu-file-picker__name">{file ? file.name : 'No file selected'}</span>
          </label>
          <p className={`bu-file-status ${file ? 'bu-file-status--ok' : ''}`}>
            {file ? `✓ ${file.name}` : 'No file selected'}
          </p>

          <label className="bu-checkbox-row">
            <input
              type="checkbox"
              checked={replaceAll}
              onChange={(e) => setReplace(e.target.checked)}
            />
            Replace all existing records
          </label>

          <button
            className="bu-btn bu-btn--primary"
            disabled={!file || uploading}
            onClick={upload}
          >
            {uploading ? '⏳ Uploading…' : '⬆ Upload Sectorial CSV'}
          </button>

          <ProgressBar pct={progress} />
          <ResultBox result={result} onClear={() => setResult(null)} />
        </div>

        {/* Right: format guide */}
        <div className="bu-special-card">
          <div className="bu-special-title">📋 CSV Format Guide</div>
          <p className="bu-guide-text">
            Upload a CSV matching the NSE sectorial format:
          </p>
          <div className="bu-format-hint">
            Company Name, Industry, Symbol, Series, ISIN Code
          </div>
          <div className="bu-sample-table-wrap">
            <table className="bu-sample-table">
              <thead>
                <tr>
                  {['Company Name', 'Industry', 'Symbol', 'Series', 'ISIN Code'].map(h => (
                    <th key={h}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Infosys Ltd</td><td>IT</td><td>INFY</td><td>EQ</td><td>INE009A01021</td>
                </tr>
                <tr>
                  <td>HDFC Bank Ltd</td><td>Banking</td><td>HDFCBANK</td><td>EQ</td><td>INE040A01034</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
   MAIN PAGE
───────────────────────────────────────────────────────────────── */
const TABS = [
  { key: 'market',    icon: '📈', label: 'Market Data',  badge: '3'  },
  { key: 'strategy',  icon: '🎯', label: 'Strategies',   badge: null  },
  { key: 'nakshatra', icon: '🌟', label: 'Nakshatra',    badge: null },
  { key: 'sectorial', icon: '🏭', label: 'Sectorial',    badge: null },
];

export default function BatchUpload() {
  const [activeTab, setActiveTab] = useState('market');

  return (
    <AppLayout pageBadge="📤 Manual Batch Upload">
      <div className="bu-root">
        {/* Page header */}
        <div className="bu-page-header">
          <div className="bu-page-header__title">� Manual Batch Upload</div>
          <div className="bu-page-header__sub">CS Insights Pro · Admin Only · CSV Uploader</div>
        </div>

        {/* Tab nav */}
        <div className="bu-tab-nav">
          {TABS.map(t => (
            <button
              key={t.key}
              className={`bu-tab-btn ${activeTab === t.key ? 'bu-tab-btn--active' : ''}`}
              onClick={() => setActiveTab(t.key)}
            >
              {t.icon} {t.label}
              {t.badge && (
                <span className="bu-tab-badge">{t.badge}</span>
              )}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className="bu-tab-content">
          {activeTab === 'market'    && <MarketTab    />}
          {activeTab === 'strategy'  && <StrategyTab  />}
          {activeTab === 'nakshatra' && <NakshatraTab />}
          {activeTab === 'sectorial' && <SectorialTab />}
        </div>
      </div>
    </AppLayout>
  );
}

